import React, { PropTypes } from "react";
import { FormattedMessage, intlShape } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Field, formValueSelector, reduxForm } from "redux-form";

import { EnumWrapper } from "../../../../../utils/enums";
import { LANGUAGE_CODES } from "../../../../../utils/enums/enums";
import { Render } from "../../../../common";
import { SelectInput } from "../../../../common/form";

import allReportTypes, {
  nonAdminReports,
  PAYROLL_DEDUCTIONS
} from "./enums/reportTypes";

import reportForms from "./forms";

import * as dataActions from "../../../../../utils/data/dataActions";
import { BILLING_PERIODS } from "../../../../../utils/data/models";
import { AGENT, HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER, INFORMATION_AGENT } from "../../../../../utils/Roles";
import SchedulerInformations from "../../../../../utils/SchedulerInformations";
import userHasAccess from "../../../../../utils/userHasAccess";
import validationUtils from "../../../../../utils/ValidationUtils";
import { EXPORTED_TO_ACOMBA, GENERATED, TEMPLATE } from "../invoices/enums/invoiceStatuses";
import { FINAL } from "../invoices/enums/invoiceSteps";
import { ANNUAL } from "../premiumSimulator/payrollFrequencyTypes";
import { CLIENT_REPORTS_VALIDATION_RULES } from "./clientReportsValidationRules";

let VALIDATION_RULES = {};

class ClientReportsForm extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      isPayrollDeductionsSelected: false,
      selectedReportFormat: null,
      selectedBillingPeriod: null,
      selectedYear: null,
    };

    this.handleReportTypeChange = this.handleReportTypeChange.bind(this);
    this.handleReportFormatChange = this.handleReportFormatChange.bind(this);
    this.handleBillingPeriodChange = this.handleBillingPeriodChange.bind(this);
    this.handleYearChange = this.handleYearChange.bind(this);
    this.getInvoicedBillingPeriods = this.getInvoicedBillingPeriods.bind(this);
  }

  componentDidMount() {
    const { clientId, dataActions } = this.props;
    dataActions.getDataList(
      BILLING_PERIODS,
      0,
      0,
      "",
      ["startDate:desc"],
      ["billingPeriods.clientId==" + clientId],
      {},
      {},
      "billingPeriodId,localizations,startDate,endDate,isDelayed,invoice(invoiceStatusType,invoiceStep,invoiceType)"
    );
  }

  componentWillUnmount() {
    this.props.dataActions.clearDataList(BILLING_PERIODS);
  }

  getInvoicedBillingPeriods() {
    return this.props.billingPeriods.filter(
      (billingPeriod) =>
        billingPeriod.invoice &&
        billingPeriod.invoice.invoiceType != TEMPLATE.value &&
        (billingPeriod.invoice.invoiceStatusType == GENERATED.value ||
          billingPeriod.invoice.invoiceStatusType == EXPORTED_TO_ACOMBA.value
        )
    );
  }

  handleReportTypeChange(event) {
    this.setState({
      isPayrollDeductionsSelected:
        event.target.value == PAYROLL_DEDUCTIONS.value,
    });
    // Clear all subForm values;
    this.props.initialize({ reportType: event.target.value });
  }

  formatEventValue(event) {
    let selectedValue = "";
    let i = 0;

    while (!!event[i]) {
      selectedValue += event[i];
      i++;
    }
    return selectedValue;
  }

  // Only used for payroll deductions reports
  handleReportFormatChange(event) {
    const selectedFormat = this.formatEventValue(event);
    this.setState({
      selectedReportFormat: selectedFormat,
    });
  }

  // Only used for payroll deductions reports
  handleYearChange(event) {
    const selectedYear = this.formatEventValue(event);
    this.setState({
      selectedYear: selectedYear,
    });
  }

  // Only used for payroll deductions reports
  handleBillingPeriodChange(event) {
    const { billingPeriods } = this.props;
    let selectedBillingPeriodId = this.formatEventValue(event);

    const billingPeriod = billingPeriods.find(
      (bp) => bp.billingPeriodId == selectedBillingPeriodId
    );

    this.setState({
      selectedBillingPeriod: billingPeriod,
    });
  }

  isYearFinalized() {
    const { selectedYear } = this.state;
    const latestBillingPeriod = this.props.billingPeriods[0];

    // we only want to check if the latest billing period is not finalized
    return latestBillingPeriod.startDate.includes(selectedYear) &&
      latestBillingPeriod.endDate.includes(selectedYear) &&
      (!latestBillingPeriod.invoice ||
        latestBillingPeriod.invoice.invoiceStep != FINAL.value)
      ? [false, latestBillingPeriod]
      : [true, null];
  }

  isBillingPeriodFinalized() {
    const { selectedBillingPeriod } = this.state;
    return !!selectedBillingPeriod && !!selectedBillingPeriod.invoice
      ? [
        selectedBillingPeriod.invoice.invoiceStep == FINAL.value,
        selectedBillingPeriod,
      ]
      : [false, selectedBillingPeriod];
  }

  renderSubForm() {
    const { clientId, reportType, billingPeriods } = this.props;
    let SubForm = null;

    if (!!reportForms[reportType]) {
      SubForm = reportForms[reportType].form;
    }

    return (
      !!SubForm && (
        <SubForm
          clientId={clientId}
          billingPeriods={billingPeriods}
          handleReportFormatChange={this.handleReportFormatChange}
          handleBillingPeriodChange={this.handleBillingPeriodChange}
          handleYearChange={this.handleYearChange}
        />
      )
    );
  }

  getAvailableReports() {
    if (userHasAccess([AGENT, INFORMATION_AGENT], true)) {
      return [];
    }
    const isAdmin = userHasAccess(HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER);
    return isAdmin ? allReportTypes : nonAdminReports;
  }

  checkWarningMessage() {
    const {
      selectedReportFormat,
      selectedYear,
      selectedBillingPeriod,
    } = this.state;

    if (!!selectedReportFormat) {
      if (selectedReportFormat == ANNUAL.value && !!selectedYear)
        return this.isYearFinalized();
      else if (selectedReportFormat != ANNUAL.value && !!selectedBillingPeriod)
        return this.isBillingPeriodFinalized();
    }
    return [true, null];
  }

  render() {
    const {
      invalid,
      generating,
      reportType,
      languageCode,
      billingPeriods,
    } = this.props;
    const { isPayrollDeductionsSelected } = this.state;
    const billingPeriodsInvoiced = this.getInvoicedBillingPeriods();
    const disableSubmit =
      invalid || generating || languageCode === " " || reportType === " " || (isPayrollDeductionsSelected && !(Array.isArray(billingPeriodsInvoiced) && billingPeriodsInvoiced.length > 0));
    let isBillingPeriodFinalized = true;
    let notFinalizedBillingPeriod = null;

    const availableReports = this.getAvailableReports();

    if (isPayrollDeductionsSelected)
      [
        isBillingPeriodFinalized,
        notFinalizedBillingPeriod,
      ] = this.checkWarningMessage();

    return (
      <div>
        <SchedulerInformations activeBillingPeriods={billingPeriods} />
        {!isBillingPeriodFinalized && (
          <div className="client-report-warning-message">
            <FormattedMessage
              id="clients.client.reports.header.warningMessage"
              values={{
                billingPeriod: !!notFinalizedBillingPeriod
                  ? notFinalizedBillingPeriod.localizations[
                    localStorage.getItem("languageCode")
                  ].name
                  : null,
              }}
            />
          </div>
        )}
        <form
          onSubmit={this.props.handleSubmit}
          className="standalone-form import-export"
        >
          <Field
            id="reportType"
            name="reportType"
            component={SelectInput}
            options={availableReports}
            addBlankOption={true}
            props={{ onChange: this.handleReportTypeChange }}
            label={<FormattedMessage id="clients.client.reports.form.type" />}
          />

          {this.renderSubForm()}

          <Render if={!!reportType}>
            <EnumWrapper enum={LANGUAGE_CODES} format="inputs" prop="options">
              <Field
                id="languageCode"
                name="languageCode"
                component={SelectInput}
                addBlankOption={true}
                label={
                  <FormattedMessage id="clients.client.reports.form.languageCode" />
                }
              />
            </EnumWrapper>
          </Render>

          <div className="import-export-submit">
            <span className="import-export-button">
              <button
                type="submit"
                className="btn btn-lg btn-primary"
                disabled={disableSubmit}
              >
                <FormattedMessage id="clients.client.reports.form.generate" />
              </button>
            </span>

            {generating && (
              <span className="import-export-loader">
                <span className="loader">
                  <FormattedMessage id="clients.client.reports.form.generating" />
                </span>
              </span>
            )}
          </div>
        </form>
      </div>
    );
  }
}

ClientReportsForm.propTypes = {
  clientId: PropTypes.number.isRequired,
  generating: PropTypes.bool,
  handleSubmit: PropTypes.func,
  initialize: PropTypes.func,
  invalid: PropTypes.bool,
  languageCode: PropTypes.string,
  reportType: PropTypes.string,
  billingPeriods: PropTypes.array,
  dataActions: PropTypes.object,
  clientStatusType: PropTypes.string
};

ClientReportsForm.contextTypes = {
  intl: intlShape,
};

function mapStateToProps(state) {
  const selector = formValueSelector("clientReportsForm");
  const reportType = selector(state, "reportType");
  const languageCode = selector(state, "languageCode");
  const billingPeriods = state.data.billingPeriods || {};
  const billingPeriodItems = billingPeriods.items || [];

  // Add selected subForm's validation to basic one
  VALIDATION_RULES = {
    ...CLIENT_REPORTS_VALIDATION_RULES,
    ...(reportForms[reportType] ? reportForms[reportType].rules : {}),
  };

  return {
    languageCode,
    reportType,
    billingPeriods: billingPeriodItems,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  withRef: true,
})(
  reduxForm({
    form: "clientReportsForm",
    validate: (values) => validationUtils.validate(values, VALIDATION_RULES),
  })(ClientReportsForm)
);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/reports/ClientReportsForm.js